import { lazy, Suspense, useCallback, useEffect, useId, useRef, useState, type KeyboardEvent, type RefObject } from "react";
import { Link, useLocation } from "react-router-dom";
import { SUPPORT_AREA_LABEL, type DocsAnswer, type SupportKind, type SupportRequest, type SupportRequestsResponse } from "@lms/shared-types";
import { ApiRequestError, fetchJson } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import type { Doc } from "../../docs";
import { Button, ErrorState, RelativeTime, StatusPill, inputClass, labelClass, useModalDialog } from "../ui";
import { searchShelf, type ShelfHit } from "./searchShelf";
import { KIND_NOUN, KIND_TITLE, SupportRequestDialog, type PageContext } from "./SupportRequestDialog";

/**
 * The support panel (PLT-09; gairm/lms#220; crmarr/templates in-app-support-panel.md): one click, or the `?` key,
 * from every signed-in page, and it opens over the page rather than taking the person anywhere — the page they were
 * on is the most useful fact in what they send.
 *
 * A native <dialog> as a sheet from the right (`useModalDialog`: the focus trap, Esc, the inert page behind, and
 * focus back on the ? when it closes); the request form opens as a second modal on top, and Esc closes only that,
 * returning focus to the card that opened it. Two tabs — Get help (ask or search the docs shelf, or open a ticket,
 * request a feature, report a bug) and My requests (the caller's own, with the number to quote and the desk's case
 * reference once it has one).
 *
 * Status inside the panel is said inside it: the shell's live region is behind the modal, and inert content is not
 * announced. The status element is always mounted and filled after, so a screen reader hears the change — and a
 * repeated identical result is emptied first, so it is heard again.
 */
const STATUS_LABEL: Record<SupportRequest["status"], string> = { open: "Open", in_progress: "Being looked at", waiting: "Waiting for you", closed: "Closed" };
const KIND_HELP: Record<SupportKind, string> = {
  ticket: "Something is not working for you and you need a hand",
  feature: "Something this should do, or do better",
  bug: "Something did the wrong thing, or nothing",
};

// The answer's Markdown renderer is its own chunk, like the shelf: not in the shell's bundle for every page. It is
// fetched when the panel opens, long before an answer comes back from the model.
const loadAnswerMarkdown = () => import("./AnswerMarkdown");
const AnswerMarkdown = lazy(() => loadAnswerMarkdown().then((m) => ({ default: m.AnswerMarkdown })));

/** The page's own name: its h1, else the document title without the product's name after the dash. */
function currentPageTitle(): string {
  const h1 = document.querySelector("main h1")?.textContent?.trim();
  if (h1) return h1;
  return document.title.replace(/\s+—\s+[^—]+$/, "").trim();
}

/** Set a status element's words so a screen reader hears them every time, even when they are the same as last time. */
function useStatus(): [string, (text: string) => void] {
  const [text, setText] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const say = useCallback((next: string) => {
    setText("");
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setText(next), 50);
  }, []);
  return [text, say];
}

export function SupportPanel({ onClose, returnTo }: { onClose: () => void; returnTo?: RefObject<HTMLElement | null> }) {
  const ref = useRef<HTMLDialogElement>(null);
  const location = useLocation();
  const titleId = useId();
  const tabIds = { help: useId(), mine: useId() };
  const [tab, setTab] = useState<"help" | "mine">("help");
  const [form, setForm] = useState<SupportKind | null>(null);
  // Read once, when the panel opens: the page is already on screen, and it is the page the person means.
  const [page] = useState<PageContext>(() => ({ title: currentPageTitle(), path: location.pathname + location.search }));
  useModalDialog(ref, onClose, returnTo);
  useEffect(() => {
    void loadAnswerMarkdown().catch(() => undefined);
  }, []);
  const closeForm = useCallback(() => setForm(null), []);

  const tabs: Array<{ key: "help" | "mine"; label: string }> = [
    { key: "help", label: "Get help" },
    { key: "mine", label: "My requests" },
  ];
  // The tabs pattern: arrows move between them, the selected one is the only tab stop.
  const onTabKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft" && e.key !== "Home" && e.key !== "End") return;
    e.preventDefault();
    const next = e.key === "Home" ? "help" : e.key === "End" ? "mine" : tab === "help" ? "mine" : "help";
    setTab(next);
    document.getElementById(tabIds[next])?.focus();
  };

  return (
    <dialog
      ref={ref}
      tabIndex={-1}
      aria-labelledby={titleId}
      data-testid="support-panel"
      className="m-0 ml-auto h-dvh max-h-none w-full max-w-md overflow-hidden border-l border-line bg-surface p-0 text-sm text-ink shadow-2xl backdrop:bg-black/40"
    >
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-3">
          <h2 id={titleId} className="text-lg font-semibold">
            Help
          </h2>
          <Button onClick={onClose}>Close</Button>
        </div>
        <div role="tablist" aria-label="Help" className="flex gap-1 border-b border-line px-5">
          {tabs.map((t) => (
            <button
              key={t.key}
              id={tabIds[t.key]}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              aria-controls={`${tabIds[t.key]}-panel`}
              tabIndex={tab === t.key ? 0 : -1}
              onClick={() => setTab(t.key)}
              onKeyDown={onTabKey}
              className={`min-h-11 border-b-2 px-3 font-medium ${tab === t.key ? "border-accent text-ink" : "border-transparent text-muted hover:text-ink"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div id={`${tabIds[tab]}-panel`} role="tabpanel" aria-labelledby={tabIds[tab]} className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {tab === "help" ? <HelpTab page={page} onOpen={setForm} onNavigate={onClose} /> : <MineTab />}
        </div>
      </div>
      {form && <SupportRequestDialog kind={form} page={page} onClose={closeForm} />}
    </dialog>
  );
}

type Result = { kind: "answer"; q: string; answer: DocsAnswer } | { kind: "search"; q: string; hits: ShelfHit[]; why?: string };

/** Why Ask did not answer, in words, for the search that stands in for it. */
function askFallback(err: unknown): string {
  const code = err instanceof ApiRequestError ? err.code : "";
  if (code === "ASK_NOT_ENABLED") return "Answers are not switched on here yet, so this is a search of the help instead.";
  if (code === "ASK_BUSY") return "Answers are busy right now, so this is a search of the help instead.";
  if (code === "DAILY_CAP") return "Today's questions for your organisation are used up, so this is a search of the help instead.";
  return "Answers are not working right now, so this is a search of the help instead.";
}

function HelpTab({ page, onOpen, onNavigate }: { page: PageContext; onOpen: (k: SupportKind) => void; onNavigate: () => void }) {
  const { session, config } = useAuth();
  const ask = config.features?.ask === true;
  const questionId = useId();
  const [question, setQuestion] = useState("");
  const [result, setResult] = useState<Result | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [status, say] = useStatus();

  // The shelf is its own chunk (App.tsx): fetched when somebody searches, not with the shell.
  const search = useCallback(
    async (q: string, why?: string) => {
      try {
        const shelf = await import("../../docs");
        const hits = searchShelf(shelf.SHELF as readonly Doc[], q, shelf.slugify);
        setResult({ kind: "search", q, hits, ...(why ? { why } : {}) });
        setLoadError(false);
        const count = hits.length === 0 ? `Nothing in the help matches “${q}”. You can tell us about it below instead.` : `${hits.length} ${hits.length === 1 ? "place" : "places"} in the help ${hits.length === 1 ? "matches" : "match"} “${q}”.`;
        say(why ? `${why} ${count}` : count);
      } catch {
        setLoadError(true);
      }
    },
    [say],
  );

  const submit = useCallback(
    async (q: string) => {
      if (!ask || !session) return search(q);
      setBusy(true);
      try {
        const answer = await fetchJson<DocsAnswer>(config, session, "/v1/support/ask", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ question: q }) });
        setResult({ kind: "answer", q, answer });
        say(answer.answered ? "Answered from the help." : "The help does not cover that. You can tell us about it below.");
      } catch (err) {
        await search(q, askFallback(err));
      } finally {
        setBusy(false);
      }
    },
    [ask, session, config, search, say],
  );

  return (
    <div className="grid gap-6">
      <section aria-labelledby={`${questionId}-h`} className="grid gap-2">
        <h3 id={`${questionId}-h`} className="text-base font-semibold">
          {ask ? "Ask a question" : "Search the help"}
        </h3>
        <form
          role="search"
          className="grid gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (question.trim()) void submit(question.trim());
          }}
        >
          <label htmlFor={questionId} className={labelClass}>
            What do you need help with?
          </label>
          <div className="flex min-w-0 gap-2">
            <input id={questionId} type="search" value={question} onChange={(e) => setQuestion(e.target.value)} maxLength={500} className={`${inputClass} min-w-0 flex-1`} autoComplete="off" />
            <Button type="submit" disabled={!question.trim() || busy}>
              {busy ? "Asking…" : ask ? "Ask" : "Search"}
            </Button>
          </div>
          <p className="text-muted">{ask ? "Answered from the help pages and release notes, naming the page the answer comes from." : "Searches the help pages and release notes."}</p>
        </form>
        {/* Always mounted, filled after: a region that appears with its text already in it is often not read. */}
        <p role="status" data-testid="support-search-status" className="font-medium empty:hidden">
          {status}
        </p>
        {loadError && (
          <p role="alert" className="text-danger">
            The help pages could not be loaded; try again, or tell us below.
          </p>
        )}
        {result?.kind === "answer" && (
          <div className="grid gap-2 rounded-lg border border-line bg-canvas p-3" data-testid="support-answer">
            <div data-testid="support-answer-text">
              <Suspense fallback={null}>
                <AnswerMarkdown source={result.answer.answer} onNavigate={onNavigate} />
              </Suspense>
            </div>
            {result.answer.citations.length > 0 && (
              <div>
                <p className="text-muted">From the help:</p>
                <ul className="grid gap-1">
                  {result.answer.citations.map((c) => (
                    <li key={`${c.slug}#${c.heading?.id ?? ""}`} className="min-w-0">
                      <Link to={`/docs/${c.slug}${c.heading ? `#${c.heading.id}` : ""}`} onClick={onNavigate} className="break-words">
                        {c.heading ? `${c.title} › ${c.heading.text}` : c.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p className="text-xs text-muted">
              {result.answer.remaining} {result.answer.remaining === 1 ? "question" : "questions"} left today for your organisation.
            </p>
          </div>
        )}
        {result?.kind === "search" && result.hits.length > 0 && (
          <ul className="grid gap-2" data-testid="support-search-results">
            {result.hits.map((h) => (
              <li key={`${h.slug}#${h.heading?.id ?? ""}`} className="min-w-0 rounded-lg border border-line p-3">
                <Link to={`/docs/${h.slug}${h.heading ? `#${h.heading.id}` : ""}`} onClick={onNavigate} className="font-medium break-words">
                  {h.heading ? `${h.title} › ${h.heading.text}` : h.title}
                </Link>
                {h.snippet && <p className="mt-1 break-words text-muted">{h.snippet}</p>}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby={`${questionId}-contact`} className="grid gap-2">
        <h3 id={`${questionId}-contact`} className="text-base font-semibold">
          Or tell us about it
        </h3>
        <p className="text-muted">{page.title ? `It is sent with the page you are on — ${page.title} — and a number you can quote.` : "It is sent with the page you are on, and a number you can quote."}</p>
        <ul className="grid gap-2">
          {(["ticket", "feature", "bug"] as const).map((k) => (
            <li key={k}>
              <button type="button" onClick={() => onOpen(k)} data-testid={`support-open-${k}`} className="flex min-h-11 w-full flex-col items-start rounded-lg border border-line px-4 py-3 text-left hover:bg-canvas">
                <span className="font-medium">{KIND_TITLE[k]}</span>
                <span className="text-muted">{KIND_HELP[k]}</span>
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function MineTab() {
  const { session, config } = useAuth();
  const [state, setState] = useState<{ loading: boolean; requests: SupportRequest[]; error?: unknown }>({ loading: true, requests: [] });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!session) return;
    let live = true;
    fetchJson<SupportRequestsResponse>(config, session, "/v1/support/requests")
      .then((r) => live && setState({ loading: false, requests: r.requests }))
      .catch((error: unknown) => live && setState({ loading: false, requests: [], error }));
    return () => {
      live = false;
    };
  }, [session, config, tick]);

  if (state.loading) return <p role="status">Loading your requests…</p>;
  if (state.error !== undefined) return <ErrorState title="Your requests could not be loaded" error={state.error} onRetry={() => setTick((t) => t + 1)} />;
  if (state.requests.length === 0) return <p className="text-muted">Nothing sent yet. What you send from Get help is listed here, with its number.</p>;
  return (
    <ul className="grid gap-3" data-testid="support-mine">
      {state.requests.map((r) => (
        <li key={r.id} className="min-w-0 rounded-lg border border-line p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-medium">
              {KIND_NOUN[r.kind]} {r.number}
            </span>
            <StatusPill tone={r.status === "closed" ? "ok" : "neutral"}>{STATUS_LABEL[r.status]}</StatusPill>
          </div>
          <p className="mt-1 text-muted">{SUPPORT_AREA_LABEL[r.area]}</p>
          <p className="mt-1 line-clamp-2 break-words">{r.body}</p>
          <p className="mt-1 text-xs text-muted" data-testid="support-handoff">
            {r.handoff.status === "handed_off" ? (r.handoff.caseReference ? `With the support desk as case ${r.handoff.caseReference}.` : "With the support desk.") : "Recorded here; not yet passed to the support desk."}
          </p>
          <p className="mt-1 text-xs text-muted">
            Sent <RelativeTime iso={r.createdAt} />
            {r.page.title ? ` from ${r.page.title}` : ""}
          </p>
        </li>
      ))}
    </ul>
  );
}
